import { RequestStatus, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';

export const
    accountingData = entityActionSet(defaults.AccountingData, {
        loadAll: networkAction('LOAD_ALL_MANUAL_ACCOUNTING_DATA', getAPIMethod('AccountingDataApi', 'loadAllManualAccounts')),
        addAll: networkAction('ADD_ALL_ACCOUNTING_DATAS', getAPIMethod('AccountingDataApi', 'addAll')),
        editAll: networkAction('EDIT_ALL_ACCOUNTING_DATAS', getAPIMethod('AccountingDataApi', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_ACCOUNTING_DATAS', getAPIMethod('AccountingDataApi', 'deleteAll'))
    }),

    loadAccountingDataPreviewReport = (rule: entities.IntegrationDataMappingRule, offset: number) =>
        networkAction(
            'LOAD_ACCOUNTING_DATA_PREVIEW_REPORT',
            { offset, rule },
            getAPIMethod<entities.AccountingDataPreviewReport[]>('AccountingDataPreviewReportApi', 'loadReport'),
            rule,
            offset
        ),

    getUsedAccountsFromAccountRange = (performanceIndicatorID: number) =>
        networkAction(
            'GET_USED_ACCOUNTS_FROM_ACCOUNT_RANGE',
            { performanceIndicatorID },
            getAPIMethod<number[]>('AccountingDataApi', 'getUsedAccountsFromAccountRange'),
            performanceIndicatorID
        ),


    integrationDataMappingRules = entityActionSet(defaults.IntegrationDataMappingRule, {
        loadAll: networkAction('LOAD_ALL_INTEGRATION_DATA_MAPPING_RULES', getAPIMethod('IntegrationDataMappingRuleApi', 'loadAll')),
        add: networkAction('ADD_INTEGRATION_DATA_MAPPING_RULE', getAPIMethod('IntegrationDataMappingRuleApi', 'add')),
        edit: networkAction('EDIT_INTEGRATION_DATA_MAPPING_RULE', getAPIMethod('IntegrationDataMappingRuleApi', 'edit')),
        delete: networkAction('DELETE_INTEGRATION_DATA_MAPPING_RULE', getAPIMethod('IntegrationDataMappingRuleApi', 'delete'))
    }),

    { loadAll: loadAllIntegrationDataMappingDimensions } = entityActionSet(defaults.IntegrationDataMappingDimension, {
        loadAll: networkAction('LOAD_ALL_INTEGRATION_DATA_MAPPING_DIMENSIONS', getAPIMethod('IntegrationDataMappingDimensionApi', 'loadAll'))
    }),

    { loadAll: loadAllIntegrationDataMappingMatchRules } = entityActionSet(defaults.IntegrationDataMappingMatchRule, {
        loadAll: networkAction('LOAD_ALL_INTEGRATION_DATA_MAPPING_MATCH_RULES', getAPIMethod('IntegrationDataMappingMatchRuleApi', 'loadAll'))
    }),

    { loadAll: loadAllIntegrationDataMappingSources } = entityActionSet(defaults.IntegrationDataMappingSource, {
        loadAll: networkAction('LOAD_ALL_INTEGRATION_DATA_MAPPING_SOURCES', getAPIMethod('IntegrationDataMappingSourceApi', 'loadAll'))
    }),


    loadInvoicingDataPreviewReport = (rule: entities.IntegrationDataMappingRule, offset: number) =>
        networkAction(
            'LOAD_INVOICING_DATA_PREVIEW_REPORT',
            { offset, rule },
            getAPIMethod<entities.InvoicingDataPreviewReport[]>('InvoicingDataPreviewReportApi', 'loadReport'),
            rule,
            offset
        ),

    dropFinancialInformation = (financialInformationKey: string) => action('DROP_ORGANIZATION_FINANCIAL_INFORMATION', { financialInformationKey }),


    erpAdminCostDivisionRules = {
        ...entityActionSet(defaults.ERPAdminCostDivisionRuleReport, {
            loadAll: networkAction('LOAD_ALL_ERP_ADMIN_COST_DIVISION_RULES', getAPIMethod('ERPAdminCostDivisionRuleApi', 'loadAll')),
            load: networkAction('LOAD_ERP_ADMIN_COST_DIVISION_RULE', getAPIMethod('ERPAdminCostDivisionRuleApi', 'load')),
            edit: networkAction('EDIT_ERP_ADMIN_COST_DIVISION_RULE', getAPIMethod('ERPAdminCostDivisionRuleApi', 'edit')),
            delete: networkAction('DELETE_ERP_ADMIN_COST_DIVISION_RULE', getAPIMethod('ERPAdminCostDivisionRuleApi', 'delete'))
        }),
        add: (entity: entities.ERPAdminCostDivisionRuleReport, sync: boolean, reloadAfterSync = false):
        NetworkAction<'ADD_ERP_ADMIN_COST_DIVISION_RULE', {entityID: number; entity: entities.ERPAdminCostDivisionRuleReport;}, number> =>
            async(dispatch: any) => {
                dispatch(action('ADD_ERP_ADMIN_COST_DIVISION_RULE', { entity, entityID: entity.ID, status: RequestStatus.UNSYNCED }));

                if (!sync) { return null; }

                const newID = await dispatch(networkAction(
                    'ADD_ERP_ADMIN_COST_DIVISION_RULE',
                    { entity, entityID: entity.ID },
                    getAPIMethod('ERPAdminCostDivisionRuleApi', 'add'),
                    entity
                ));

                if (newID && reloadAfterSync) { dispatch(erpAdminCostDivisionRules.load(newID)); }

                return newID;
            }
    },

    { loadAll: loadAllERPAdminCostDivisionTypes } = entityActionSet(defaults.ERPAdminCostDivisionType, {
        loadAll: networkAction('LOAD_ALL_ERP_ADMIN_COST_DIVISION_TYPES', getAPIMethod('ERPAdminCostDivisionTypeApi', 'loadAll'))
    }),

    erpAdminCostDivisionRuleSourceFilter = entityActionSet(defaults.ERPAdminCostDivisionRuleReport, defaults.ERPAdminCostDivisionRuleSourceFilter, {
        edit: networkAction('EDIT_ERP_ADMIN_COST_DIVISION_RULE_SOURCE_FILTER', getAPIMethod('ERPAdminCostDivisionRuleSourceFilter', 'edit'))
    }),

    erpAdminCostDivisionRuleFromAccounts = entityActionSet(defaults.ERPAdminCostDivisionRuleReport, defaults.ERPAdminCostDivisionRuleFromAccount, {
        addAll: networkAction('ADD_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FROM_ACCOUNTS', getAPIMethod('ERPAdminCostDivisionRuleFromAccountApi', 'addAll')),
        deleteAll: networkAction('DELETE_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FROM_ACCOUNTS', getAPIMethod('ERPAdminCostDivisionRuleFromAccountApi', 'deleteAll'))
    }),

    erpAdminCostDivisionRuleFixedRules = entityActionSet(defaults.ERPAdminCostDivisionRuleReport, defaults.ERPAdminCostDivisionRuleFixedRule, {
        addAll: networkAction('ADD_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FIXED_RULES', getAPIMethod('ERPAdminCostDivisionRuleFixedRuleApi', 'addAll')),
        editAll: networkAction('EDIT_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FIXED_RULES', getAPIMethod('ERPAdminCostDivisionRuleFixedRuleApi', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FIXED_RULES', getAPIMethod('ERPAdminCostDivisionRuleFixedRuleApi', 'deleteAll'))
    }),

    erpAdminCostDivisionRuleLevels = entityActionSet(defaults.ERPAdminCostDivisionRuleReport, defaults.ERPAdminCostDivisionRuleLevel, {
        addAll: networkAction('ADD_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_LEVELS', getAPIMethod('ERPAdminCostDivisionRuleLevelApi', 'addAll')),
        editAll: networkAction('EDIT_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_LEVELS', getAPIMethod('ERPAdminCostDivisionRuleLevelApi', 'editAll'))
    }),


    erpCorporateAccounts = entityActionSet(defaults.ERPCorporateAccount, {
        loadAll: networkAction('LOAD_ALL_ERP_CORPORATE_ACCOUNTS', getAPIMethod('ERPCorporateAccountApi', 'loadAll')),
        addAll: networkAction('ADD_ALL_ERP_CORPORATE_ACCOUNTS', getAPIMethod('ERPCorporateAccountApi', 'addAll')),
        editAll: networkAction('EDIT_ALL_ERP_CORPORATE_ACCOUNTS', getAPIMethod('ERPCorporateAccountApi', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_ERP_CORPORATE_ACCOUNTS', getAPIMethod('ERPCorporateAccountApi', 'deleteAll'))
    }),


    erpDimensionMapping = {
        load: () => networkAction('LOAD_ERP_DIMENSION_MAPPING', getAPIMethod<entities.ERPDimensionMapping>('ERPDimensionMappingApi', 'load')),
        activateDimension: (dimension: number, name: string, options: { trackingLevelID?: number; trackingLevelName?: string; } = {}) =>
            networkAction(
                'ACTIVATE_ERP_DIMENSION_MAPPING_DIMENSION',
                { dimension, name, ...options },
                getAPIMethod<entities.ERPDimensionMapping>('ERPDimensionMappingApi', 'activateDimension'),
                dimension,
                name,
                options
            ),
        disableDimension: (dimension: number) =>
            networkAction('DISABLE_ERP_DIMENSION_MAPPING_DIMENSION', { dimension }, getAPIMethod('ERPDimensionMappingApi', 'disableDimension'), dimension),
        void: (immediately = false) => action('VOID_ERP_DIMENSION_MAPPING', { payload: { immediately } })
    },

    erpDimensionSyncSettings = entityActionSet(defaults.ERPDimensionSyncSetting, {
        loadAll: networkAction('LOAD_ALL_ERP_DIMENSION_SYNC_SETTINGS', getAPIMethod('ERPDimensionSyncSetting', 'loadAll')),
        addAll: networkAction('ADD_ALL_ERP_DIMENSION_SYNC_SETTINGS', getAPIMethod('ERPDimensionSyncSetting', 'addAll')),
        editAll: networkAction('EDIT_ALL_ERP_DIMENSION_SYNC_SETTINGS', getAPIMethod('ERPDimensionSyncSetting', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_ERP_DIMENSION_SYNC_SETTINGS', getAPIMethod('ERPDimensionSyncSetting', 'deleteAll'))
    });

export type FinancialManagementAction = ExtractAction<
| typeof accountingData
| typeof loadAccountingDataPreviewReport
| typeof getUsedAccountsFromAccountRange
| typeof integrationDataMappingRules
| typeof loadAllIntegrationDataMappingDimensions
| typeof loadAllIntegrationDataMappingMatchRules
| typeof loadAllIntegrationDataMappingSources
| typeof loadInvoicingDataPreviewReport
| typeof dropFinancialInformation
| typeof erpAdminCostDivisionRules
| typeof loadAllERPAdminCostDivisionTypes
| typeof erpAdminCostDivisionRuleSourceFilter
| typeof erpAdminCostDivisionRuleFromAccounts
| typeof erpAdminCostDivisionRuleFixedRules
| typeof erpAdminCostDivisionRuleLevels
| typeof erpCorporateAccounts
| typeof erpDimensionMapping
| typeof erpDimensionSyncSettings
>;
